import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { Loader2, MapPin, CalendarDays, Wallet, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';

import {
	ApiTypes,
	IGetJobListingDetailsFromSlugResponseData,
} from '@/types/server/api.types';
import posthog from 'posthog-js';
import { formatDateToHumanReadable } from '@/utils/date.utils';
import { LoginButton } from '@/components/custom/LoginButton.components';
import { logError } from '@/utils/logger.utils';

export const JobListingPage = ({
	isUserLoggedIn,
	setIsUserLoggedIn,
	setUserDetails,
}: {
	isUserLoggedIn: boolean;
	setIsUserLoggedIn: (isUserLoggedIn: boolean) => void;
	setUserDetails: (
		userDetails: ApiTypes.TGetCandidateDetailsResponse['data']
	) => void;
}) => {
	const { slug } = useParams();
	const [listingDetails, setListingDetails] =
		useState<IGetJobListingDetailsFromSlugResponseData | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [jobListingFetchMessage, setJobListingFetchMessage] = useState<
		string | null
	>(null);
	const [logoLoadErrors, setLogoLoadErrors] = useState<{
		main: boolean;
		sidebar: boolean;
		mobile: boolean;
	}>({
		main: false,
		sidebar: false,
		mobile: false,
	});

	// get query params
	const [params] = useSearchParams();
	const navigate = useNavigate();
	useEffect(() => {
		const fetchListingDetails = async () => {
			try {
				const response = await fetch(
					`${import.meta.env.VITE_SERVER_API_PREFIX}/job-listings/${slug}`
				);
				const data =
					(await response.json()) as ApiTypes.TGetJobListingDetailsFromSlugResponse;

				if (data.httpResponseCode !== 200) {
					const message = data.message || 'No job listing found.';
					toast('Error getting listing details', {
						description: message,
						id: 'fetch-listing-details-error',
					});
					setJobListingFetchMessage(message);
					return;
				}

				document.title = data.data?.title || 'Brivio Home';
				document
					.querySelector('meta[name="description"]')
					?.setAttribute(
						'content',
						data.data?.description.main ||
							'Apply to hundreds of top 1% high-paying jobs through 1 application on Brivio.'
					);

				setListingDetails(data.data);
			} catch {
				const fallbackMessage =
					'We are facing some trouble getting the listing details. Please check your network and refresh the page.';
				toast('Error getting listing details', {
					description: fallbackMessage,
					id: 'fetch-listing-details-error',
				});
				setJobListingFetchMessage(fallbackMessage);
			} finally {
				setIsLoading(false);
			}
		};

		posthog.capture('JobListingPage_Viewed', {
			jobListingSlug: slug,
			...Object.fromEntries(params.entries()),
		});

		fetchListingDetails();
	}, []);

	const handleApplyForJob = async (device: 'Desktop' | 'Mobile') => {
		try {
			const response = await fetch(
				`${import.meta.env.VITE_SERVER_API_PREFIX}/job-listings/${slug}/apply`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${localStorage.getItem('token')}`,
					},
				}
			);

			const data =
				(await response.json()) as ApiTypes.TGetJobListingDetailsFromSlugResponse;

			if (data.httpResponseCode !== 200) {
				throw new Error(data.message || 'Error applying for job');
			}

			// @TODO make this better - remove token if auth fails etc.

			posthog.capture(`JobListingPage_ApplyForJobClicked${device}`, {
				jobListingSlug: slug,
				...Object.fromEntries(params.entries()),
			});
		} catch (error) {
			logError(error);
		} finally {
			navigate('/');
		}
	};

	const handleLogoError = (context: 'main' | 'sidebar' | 'mobile') => {
		setLogoLoadErrors((prev) => ({ ...prev, [context]: true }));
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-white">
				<Loader2 className="h-8 w-8 animate-spin text-blue-600" />
			</div>
		);
	}

	if (!listingDetails) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-white px-6 text-center">
				<p className="text-gray-500 text-base max-w-md">
					{jobListingFetchMessage || 'No job opening found.'}
				</p>
			</div>
		);
	}

	const {
		title,
		location,
		salaryDescription,
		tags,
		description,
		createdAt,
		organization,
	} = listingDetails;

	const formatSalary = () => {
		if (!salaryDescription || !salaryDescription.currency)
			return 'Not specified';

		const { symbol } = salaryDescription.currency;
		const min = salaryDescription.min?.display;
		const max = salaryDescription.max?.display;
		const freq = salaryDescription.frequency;

		if (!min && !max) return 'Not specified';
		if (min && !max) return `${symbol}${min} / ${freq}`;
		if (!min && max) return `${symbol}${max} / ${freq}`;

		return `${symbol}${min} - ${symbol}${max} / ${freq}`;
	};

	const formattedDate = formatDateToHumanReadable(createdAt.toString());
	const formattedSalary = formatSalary();
	const hasDisplayableSalary = formattedSalary !== 'Not specified';

	return (
		<div className="bg-gray-50 min-h-screen px-4 py-10">
			<div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-6">
				<div className="lg:col-span-2 space-y-6">
					<div className="bg-white rounded-md shadow p-6 space-y-5">
						{/* Organization Logo and Name */}
						<div className="flex items-center gap-3 pb-4 border-b border-gray-100">
							<div className="flex-shrink-0">
								{logoLoadErrors.main ? (
									<div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
										<Building className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
									</div>
								) : (
									<img
										src={organization.logoSrc}
										alt={`${organization.displayName} logo`}
										className="w-12 h-12 sm:w-16 sm:h-16 object-contain rounded-lg border border-gray-200 bg-white p-1"
										onError={() => handleLogoError('main')}
									/>
								)}
							</div>
							<div className="flex-1 min-w-0">
								<h2 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
									{organization.displayName}
								</h2>
								<p className="text-xs sm:text-sm text-gray-500">
									Hiring for this position
								</p>
							</div>
						</div>

						<div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
							<h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
								{title}
							</h1>
						</div>

						<div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-2">
							<div className="flex items-center gap-2">
								<MapPin size={16} />
								<span>
									{location.mode === 'remote'
										? 'Remote' +
										  (location.address ? ` / ${location.address}` : '')
										: location.address}
								</span>
							</div>
							{hasDisplayableSalary && (
								<div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-full border border-green-100">
									<Wallet size={16} className="text-green-600" />
									<span className="font-medium text-green-700">
										{formattedSalary}
									</span>
								</div>
							)}
							<div className="flex items-center gap-2">
								<CalendarDays size={16} />
								<span>Posted on {formattedDate}</span>
							</div>
						</div>

						{tags.length > 0 && (
							<div className="flex flex-wrap gap-2 mt-3">
								{tags.map((tag, idx) => (
									<span
										key={idx}
										className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-700"
									>
										{tag}
									</span>
								))}
							</div>
						)}
					</div>

					{/* DESCRIPTION */}
					<div className="bg-white rounded-md shadow p-6 space-y-8">
						<section>
							<h2 className="text-xl font-semibold text-gray-800 mb-3">
								About the Role
							</h2>
							<div className="space-y-4 text-gray-700 leading-relaxed">
								{description.main
									.split('<endl>')
									.filter(Boolean)
									.map((line, idx) => (
										<p key={idx}>{line}</p>
									))}
							</div>
						</section>

						<section>
							<h3 className="text-lg font-semibold text-gray-800 mb-2">
								Requirements
							</h3>
							<ul className="list-disc pl-6 space-y-2 text-gray-700">
								{description.requirements.map((req, idx) => (
									<li key={idx}>{req}</li>
								))}
							</ul>
						</section>

						<section>
							<h3 className="text-lg font-semibold text-gray-800 mb-2">
								Other Details
							</h3>
							<ul className="list-disc pl-6 space-y-2 text-gray-700">
								{description.otherDetails.map((detail, idx) => (
									<li key={idx}>{detail}</li>
								))}
							</ul>
						</section>
					</div>
				</div>

				{/* SIDEBAR - Updated for better design */}
				<div className="hidden lg:block">
					<div className="bg-white rounded-md shadow p-6 sticky top-6">
						{/* Organization info in sidebar */}
						<div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
							{logoLoadErrors.sidebar ? (
								<div className="w-10 h-10 flex items-center justify-center rounded-md border border-gray-200 bg-gray-50">
									<Building className="w-5 h-5 text-gray-400" />
								</div>
							) : (
								<img
									src={organization.logoSrc}
									alt={`${organization.displayName} logo`}
									className="w-10 h-10 object-contain rounded-md border border-gray-200 bg-white p-1"
									onError={() => handleLogoError('sidebar')}
								/>
							)}
							<div className="flex-1 min-w-0">
								<p className="text-sm font-medium text-gray-900 truncate">
									{organization.displayName}
								</p>
								<p className="text-xs text-gray-500">Organization</p>
							</div>
						</div>

						<h3 className="text-lg font-semibold text-gray-900 mb-2">
							Quick Apply
						</h3>
						<p className="text-sm text-gray-600 mb-4 text-left">
							We begin your matching process with the organization immediately
							after you apply.
						</p>
						{isUserLoggedIn ? (
							<Button
								className="w-full bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
								onClick={() => handleApplyForJob('Desktop')}
							>
								Apply for this position
							</Button>
						) : (
							<LoginButton
								setIsUserLoggedIn={setIsUserLoggedIn}
								buttonVariant="outline"
								buttonText="Login with Google to Apply"
								className="w-full cursor-pointer text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-white"
								redirectTo={`/listing/${slug}`}
								setUserDetails={setUserDetails}
							/>
						)}
					</div>
				</div>
			</div>

			{/* MOBILE STICKY - Updated for better design */}
			<div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-md px-4 py-3 flex items-center justify-between z-50">
				<div className="flex items-center gap-3 flex-1 min-w-0">
					{logoLoadErrors.mobile ? (
						<div className="w-8 h-8 flex items-center justify-center rounded border border-gray-200 bg-gray-50 flex-shrink-0">
							<Building className="w-4 h-4 text-gray-400" />
						</div>
					) : (
						<img
							src={organization.logoSrc}
							alt={`${organization.displayName} logo`}
							className="w-8 h-8 object-contain rounded border border-gray-200 bg-white p-0.5 flex-shrink-0"
							onError={() => handleLogoError('mobile')}
						/>
					)}
					<div className="flex-1 min-w-0">
						{hasDisplayableSalary ? (
							<div className="flex flex-col">
								<span className="text-sm font-bold text-green-700 truncate">
									{formattedSalary}
								</span>
								<span className="text-xs text-gray-500 truncate">
									at {organization.displayName}
								</span>
							</div>
						) : (
							<div className="flex flex-col">
								<span className="text-sm text-gray-700 truncate">
									Ready to apply?
								</span>
								<span className="text-xs text-gray-500 truncate">
									at {organization.displayName}
								</span>
							</div>
						)}
					</div>
				</div>
				<div className="ml-3">
					{isUserLoggedIn ? (
						<Button
							className="bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
							onClick={() => handleApplyForJob('Mobile')}
						>
							Apply
						</Button>
					) : (
						<LoginButton
							setIsUserLoggedIn={setIsUserLoggedIn}
							buttonVariant="outline"
							buttonText="Login to Apply"
							className="cursor-pointer text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-white"
							redirectTo={`/listing/${slug}`}
							setUserDetails={setUserDetails}
						/>
					)}
				</div>
			</div>
		</div>
	);
};
